import {
  AUSTRALIA_APPENDIX_ROUTE_LABELS,
  getAustraliaAppendixLink,
} from "./supportAustralia";
import type {
  StaticSupportPlaybook,
  StaticSupportPlaybookLink,
  StaticSupportPlaybookStep,
} from "./supportPlaybooks";

const PLAYBOOK_REVIEWED_AT = "2026-04-21T12:00:00.000Z";

const SERVICES_AUSTRALIA_PARENTING_PAYMENT =
  "https://www.servicesaustralia.gov.au/parenting-payment";
const SERVICES_AUSTRALIA_FAMILY_TAX_BENEFIT =
  "https://www.servicesaustralia.gov.au/family-tax-benefit";
const SERVICES_AUSTRALIA_CHILD_CARE_SUBSIDY =
  "https://www.servicesaustralia.gov.au/child-care-subsidy";
const SERVICES_AUSTRALIA_RENT_ASSISTANCE =
  "https://www.servicesaustralia.gov.au/rent-assistance";
const SERVICES_AUSTRALIA_PARENT_PATHWAYS =
  "https://www.servicesaustralia.gov.au/parent-pathways";
const SERVICES_AUSTRALIA_CARING_SUPPORT =
  "https://www.servicesaustralia.gov.au/getting-payment-if-youre-carer";
const FAMILY_RELATIONSHIPS_ONLINE =
  "https://www.familyrelationships.gov.au/find-local-help";
const FCFCOA_PARENTING_ORDERS =
  "https://www.fcfcoa.gov.au/fl/hdi/apply-parenting-orders";
const PASSPORT_CONSENT = "https://www.passports.gov.au/consent";
const STARTING_BLOCKS = "https://startingblocks.gov.au/";
const DISABILITY_STANDARDS =
  "https://www.education.gov.au/disability-standards-education-2005";
const NDIS_EDUCATION_SUPPORTS =
  "https://ourguidelines.ndis.gov.au/how-ndis-supports-work-menu/mainstream-and-community-supports/how-do-we-work-out-who-should-fund-or-provide-your-supports";
const BUSINESS_REGISTRATION_SERVICE = "https://register.business.gov.au/";
const GST_REGISTRATION =
  "https://business.gov.au/registrations/register-for-taxes/register-for-goods-and-services-tax-gst";
const FAIR_WORK_FLEX =
  "https://www.fairwork.gov.au/employment-conditions/flexibility-in-the-workplace/flexible-working-arrangements";
const FAIR_WORK_PARENTAL_LEAVE =
  "https://www.fairwork.gov.au/leave/parental-leave";
const FAIR_WORK_FDV =
  "https://www.fairwork.gov.au/leave/family-and-domestic-violence-leave";
const FAIR_WORK_SICK_AND_CARERS =
  "https://www.fairwork.gov.au/leave/sick-and-carers-leave";
const FAIR_WORK_EMPLOYEE_TYPES =
  "https://www.fairwork.gov.au/starting-employment/types-of-employees";
const FAIR_WORK_PAY =
  "https://www.fairwork.gov.au/pay-and-wages";
const FAIR_WORK_REDUNDANCY =
  "https://www.fairwork.gov.au/ending-employment/redundancy";
const FAIR_WORK_PROTECTIONS =
  "https://www.fairwork.gov.au/employment-conditions/protections-at-work";
const ATO_RECORD_KEEPING =
  "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/keeping-records";
const MONEYSMART_NILS = "https://moneysmart.gov.au/loans/no-interest-loans";
const MONEYSMART_BUDGET =
  "https://moneysmart.gov.au/budgeting/budget-planner";
const MONEYSMART_SUPER =
  "https://moneysmart.gov.au/how-super-works";
const MEDICARE_MENTAL_HEALTH =
  "https://www.medicarementalhealth.gov.au/";
const LIFELINE = "https://www.lifeline.org.au/";
const RESPECT = "https://www.1800respect.org.au/";

type StepInput = Omit<StaticSupportPlaybookStep, "step">;

const withLinks = (
  ...links: Array<StaticSupportPlaybookLink | null | undefined>
) => links.filter((link): link is StaticSupportPlaybookLink => Boolean(link));

const appendices = (postcode?: string | null) => ({
  tenancy: getAustraliaAppendixLink(
    AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy,
    postcode,
  ),
  socialHousing: getAustraliaAppendixLink(
    AUSTRALIA_APPENDIX_ROUTE_LABELS.socialHousing,
    postcode,
  ),
  educationStart: getAustraliaAppendixLink(
    AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart,
    postcode,
  ),
  homeEducation: getAustraliaAppendixLink(
    AUSTRALIA_APPENDIX_ROUTE_LABELS.homeEducation,
    postcode,
  ),
  legalAid: getAustraliaAppendixLink(
    AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid,
    postcode,
  ),
});

const buildPlaybook = (definition: {
  slug: string;
  title: string;
  description: string;
  icon: string;
  life_event_tags: string[];
  steps: StepInput[];
}): StaticSupportPlaybook => ({
  id: `static-au-${definition.slug}`,
  slug: definition.slug,
  section: "support",
  title: definition.title,
  description: definition.description,
  icon: definition.icon,
  difficulty: "moderate",
  estimated_time: `${definition.steps.length} steps`,
  life_event_tags: definition.life_event_tags,
  steps: definition.steps.map((step, index) => ({
    ...step,
    step: index + 1,
  })),
  is_active: true,
  sort_order: 0,
  last_reviewed_at: PLAYBOOK_REVIEWED_AT,
  review_frequency_months: 6,
  created_at: PLAYBOOK_REVIEWED_AT,
  updated_at: PLAYBOOK_REVIEWED_AT,
});

export const getAustraliaStaticSupportPlaybook = (
  slug: string,
  postcode?: string | null,
): StaticSupportPlaybook | null => {
  const appendixLinks = appendices(postcode);

  switch (slug) {
    case "moving-house":
      return buildPlaybook({
        slug,
        title: "Moving House as a Single Parent",
        description:
          "The full checklist from deciding to move to the first steadier week in the new place.",
        icon: "📦",
        life_event_tags: ["housing", "moving", "single parent"],
        steps: [
          {
            title: "Check your housing position first",
            description:
              "If you rent, use the tenancy rules for your own state or territory before you give notice, challenge a notice, or argue about bond, repairs, or evidence.",
            actions: [
              "Check the correct tenancy authority for your saved location.",
              "If safety is part of the move, treat that as the first task rather than ordinary moving admin.",
            ],
            links: withLinks(
              appendixLinks.tenancy,
              { label: "1800RESPECT", url: RESPECT },
            ),
          },
          {
            title: "Plan the move around the children, not just the property",
            description:
              "Think about school, childcare, handovers, commute, and whether a larger move will affect parenting arrangements or require legal advice.",
            actions: [
              "List the school, childcare, and handover implications before you commit.",
              "If the move is major or disputed, get advice before it becomes urgent.",
            ],
            links: withLinks(
              { label: "Family Relationships Online", url: FAMILY_RELATIONSHIPS_ONLINE },
              appendixLinks.educationStart,
              appendixLinks.legalAid,
            ),
          },
          {
            title: "Tell Services Australia early",
            description:
              "Address, rent, care percentage, and relationship-status changes can all affect family payments, Rent Assistance, Parenting Payment, and child support.",
            actions: [
              "Update Services Australia as soon as the move or care change is real.",
            ],
            links: withLinks(
              { label: "Services Australia — Rent Assistance", url: SERVICES_AUSTRALIA_RENT_ASSISTANCE },
              { label: "Services Australia — Family Tax Benefit", url: SERVICES_AUSTRALIA_FAMILY_TAX_BENEFIT },
            ),
          },
          {
            title: "Sort school and childcare before the move if you can",
            description:
              "Mid-year moves are possible, but things go more smoothly when the new school or service already has the paperwork and understands the family situation.",
            actions: [
              "Tell the school or service early if parenting arrangements are changing as well as the address.",
            ],
            links: withLinks(
              appendixLinks.educationStart,
              { label: "Starting Blocks", url: STARTING_BLOCKS },
            ),
          },
          {
            title: "Redirect the places that matter",
            description:
              "Update myGov, Medicare, school, childcare, employer, bank, insurer, and any child-support or court paperwork instead of relying on memory once the move is underway.",
            actions: [
              "Make one moving-admin checklist and tick it off in writing.",
            ],
          },
          {
            title: "Keep a proper leaving file",
            description:
              "Photos, condition reports, meter readings, notices, receipts, and repair messages matter if a bond or tenancy dispute shows up later.",
            actions: [
              "Save the photos and the paperwork somewhere easy to pull together fast.",
            ],
            links: withLinks(appendixLinks.tenancy),
          },
          {
            title: "Recheck support in the new place",
            description:
              "A move can change what matters most, including Rent Assistance, social housing options, concessions, school transport, and local support.",
            actions: [
              "Run the benefits and housing check again once the address is settled.",
            ],
            links: withLinks(
              appendixLinks.socialHousing,
              { label: "Services Australia — Rent Assistance", url: SERVICES_AUSTRALIA_RENT_ASSISTANCE },
            ),
          },
          {
            title: "Give yourself one essentials-first week",
            description:
              "Beds made, uniforms ready, food in the house, and one calm routine are enough for the first week. The rest can catch up after the landing is safe.",
            actions: [
              "Prioritise sleep, school, food, and one stable routine before the rest of the unpacking.",
            ],
          },
        ],
      });
    case "going-self-employed":
      return buildPlaybook({
        slug,
        title: "Setting Up Self-Employed from Home",
        description:
          "Everything to do in the first 90 days so you are legal, organised, and not surprised by tax later.",
        icon: "💻",
        life_event_tags: ["work", "self-employment", "tax"],
        steps: [
          {
            title: "Work out if this is really a business",
            description:
              "Start by checking whether you are operating a business or still in hobby territory, because that shapes the next admin step.",
            actions: [
              "Use the official business tools before you apply for registrations.",
            ],
            links: withLinks({
              label: "business.gov.au — Business structure and registrations",
              url: BUSINESS_REGISTRATION_SERVICE,
            }),
          },
          {
            title: "Choose the structure before you trade properly",
            description:
              "For many single parents, sole trader is the simplest starting point, but it still comes with legal and money responsibility.",
            actions: [
              "Choose the simplest structure that fits real life, not the one that sounds most impressive online.",
            ],
            links: withLinks({
              label: "business.gov.au — Business structure and registrations",
              url: BUSINESS_REGISTRATION_SERVICE,
            }),
          },
          {
            title: "Check the registrations you actually need",
            description:
              "ABN, business name, and GST decisions should come from the official registration tools, not social-media advice.",
            actions: [
              "Use the registration service to work out the real list for your setup.",
            ],
            links: withLinks(
              { label: "Business Registration Service", url: BUSINESS_REGISTRATION_SERVICE },
              { label: "Register for GST", url: GST_REGISTRATION },
            ),
          },
          {
            title: "Separate business money early",
            description:
              "Open a dedicated account and set one weekly admin habit now. It is much easier than untangling household and business money later.",
            actions: [
              "Keep income, expenses, and invoices in one clear place from day one.",
            ],
          },
          {
            title: "Build the record-keeping system before you are busy",
            description:
              "Income, expenses, receipts, mileage, BAS, and tax paperwork all need one calm home before the first busy month lands.",
            actions: [
              "Choose the simplest system you will actually keep using every week.",
            ],
            links: withLinks({
              label: "ATO — Record keeping for business",
              url: ATO_RECORD_KEEPING,
            }),
          },
          {
            title: "Sort childcare before you overpromise",
            description:
              "Check whether Child Care Subsidy, OSHC, or vacation care will genuinely support the hours you want to sell before you commit to work that the household cannot cover.",
            actions: [
              "Plan the care arrangement before you promise fixed hours to clients.",
            ],
            links: withLinks(
              { label: "Services Australia — Child Care Subsidy", url: SERVICES_AUSTRALIA_CHILD_CARE_SUBSIDY },
              { label: "Starting Blocks", url: STARTING_BLOCKS },
            ),
          },
          {
            title: "Plan tax and super before the first good month fools you",
            description:
              "No employer is paying super for you as a sole trader, and GST or BAS questions are easier to manage before the money feels spoken for.",
            actions: [
              "Set aside money for tax and decide how you will handle super early.",
            ],
            links: withLinks(
              { label: "Register for GST", url: GST_REGISTRATION },
              { label: "Moneysmart — Superannuation", url: MONEYSMART_SUPER },
            ),
          },
          {
            title: "Recheck Services Australia when income changes",
            description:
              "New business income can affect Parenting Payment, Family Tax Benefit, and Child Care Subsidy, especially when money arrives in uneven patterns.",
            actions: [
              "Update your estimates and circumstances instead of waiting for a later correction.",
            ],
            links: withLinks(
              { label: "Services Australia — Parenting Payment", url: SERVICES_AUSTRALIA_PARENTING_PAYMENT },
              { label: "Services Australia — Family Tax Benefit", url: SERVICES_AUSTRALIA_FAMILY_TAX_BENEFIT },
            ),
          },
        ],
      });
    case "separation-divorce":
      return buildPlaybook({
        slug,
        title: "Separation and Parenting — the First Steps",
        description:
          "What to do in the first weeks when everything feels impossible but decisions still need to be made.",
        icon: "⚖️",
        life_event_tags: ["separation", "divorce", "co-parenting"],
        steps: [
          {
            title: "Safety comes first",
            description:
              "If there is violence, coercion, or fear, treat that as the first task and get specialist support before trying to handle the separation like an ordinary breakup.",
            actions: [
              "Use specialist safety support and call police if there is immediate danger.",
            ],
            links: withLinks(
              { label: "1800RESPECT", url: RESPECT },
              appendixLinks.legalAid,
            ),
          },
          {
            title: "Secure the basics quickly",
            description:
              "Get copies of ID, bank access, Centrelink details, Medicare, school contacts, tenancy or mortgage documents, and any court paperwork into one safe place.",
            actions: [
              "Create one secure folder for the key documents and account details.",
            ],
          },
          {
            title: "Tell Services Australia when care changes",
            description:
              "Family payments, Parenting Payment, child support, and Medicare details do not update themselves after separation.",
            actions: [
              "Update care percentages and household changes early.",
            ],
            links: withLinks(
              { label: "Services Australia — Family Tax Benefit", url: SERVICES_AUSTRALIA_FAMILY_TAX_BENEFIT },
              { label: "Services Australia — Child Support", url: "https://www.servicesaustralia.gov.au/child-support" },
            ),
          },
          {
            title: "Keep children out of adult conflict",
            description:
              "Children need predictable care and reassurance, not money arguments, legal language, or emotional debriefing.",
            actions: [
              "Keep adult conflict out of handovers and school communications.",
            ],
          },
          {
            title: "Start with an interim written care arrangement if you can",
            description:
              "Even a simple written plan about days, school pickups, and holidays is clearer than relying on memory and mood.",
            actions: [
              "Write down the next practical arrangement before you argue about the whole future.",
            ],
          },
          {
            title: "Use family dispute resolution early if it is safe",
            description:
              "Many families need family dispute resolution or a Family Relationship Centre before a parenting application, unless an exemption applies.",
            actions: [
              "Check the family-dispute-resolution route before court becomes the default.",
            ],
            links: withLinks({ label: "Family Relationships Online", url: FAMILY_RELATIONSHIPS_ONLINE }),
          },
          {
            title: "Do not mix parenting arrangements up with child support",
            description:
              "These are different systems, and combining them usually makes both harder and puts children in the middle.",
            actions: [
              "Keep separate notes and separate conversations for money and care.",
            ],
            links: withLinks({ label: "Services Australia — Child Support", url: "https://www.servicesaustralia.gov.au/child-support" }),
          },
          {
            title: "Get legal help early if the separation is not straightforward",
            description:
              "Relocation, no agreement about the children, passport issues, family violence, or urgent housing pressure all justify earlier legal help, not later.",
            actions: [
              "Get advice before booking travel, moving, or missing a court deadline.",
            ],
            links: withLinks(
              { label: "FCFCOA — Parenting orders", url: FCFCOA_PARENTING_ORDERS },
              appendixLinks.legalAid,
            ),
          },
        ],
      });
    case "getting-ehcp":
      return buildPlaybook({
        slug,
        title: "Getting Support at School for Your Child",
        description:
          "The Australian school-support journey in plain English, without pretending there is one national EHCP-style system.",
        icon: "📚",
        life_event_tags: ["education", "childcare", "additional needs"],
        steps: [
          {
            title: "Write down what you are seeing",
            description:
              "Learning, behaviour, sensory issues, communication, fatigue, attendance, transitions, and exclusions are all useful evidence.",
            actions: [
              "Keep concrete examples rather than waiting for a perfect label.",
            ],
          },
          {
            title: "Ask the school for a meeting early",
            description:
              "Do not wait for a full diagnosis before you start the conversation about what is happening day to day at school.",
            actions: [
              "Ask for a meeting and follow up in writing afterwards.",
            ],
            links: withLinks(appendixLinks.educationStart),
          },
          {
            title: "Ask what reasonable adjustments can start now",
            description:
              "Seating, workload, communication supports, sensory changes, assistive technology, and transition support should not wait for months if the need is already clear.",
            actions: [
              "Ask what can start now and when it will be reviewed.",
            ],
            links: withLinks({ label: "Disability Standards for Education", url: DISABILITY_STANDARDS }),
          },
          {
            title: "Keep the paper trail calm and clear",
            description:
              "After meetings, send a short written summary of what was discussed, what was agreed, and when it will be reviewed.",
            actions: [
              "Keep dates, emails, and meeting notes together in one file.",
            ],
          },
          {
            title: "Use the Disability Standards as your rights backbone",
            description:
              "The national standards explain the obligations schools already have when disability affects access, participation, or support.",
            actions: [
              "Use the standards to frame the support conversation, not to start a fight.",
            ],
            links: withLinks({ label: "Disability Standards for Education", url: DISABILITY_STANDARDS }),
          },
          {
            title: "Keep NDIS and school responsibilities separate",
            description:
              "If NDIS is relevant, ask clearly what sits with the school and what may sit in a plan so neither system quietly pushes everything onto the other.",
            actions: [
              "Ask each side what it sees as its responsibility and write that down.",
            ],
            links: withLinks({ label: "NDIS — Education and school supports", url: NDIS_EDUCATION_SUPPORTS }),
          },
          {
            title: "Use the early-childhood route if your child is younger",
            description:
              "If concerns show up before school, ask what can happen now through early-childhood, health, and disability routes instead of waiting for school entry.",
            actions: [
              "Ask both health and education services what can start now.",
            ],
            links: withLinks(
              { label: "Starting Blocks", url: STARTING_BLOCKS },
              { label: "NDIS — Education and school supports", url: NDIS_EDUCATION_SUPPORTS },
            ),
          },
          {
            title: "Escalate through the state or territory pathway if support is not happening",
            description:
              "If agreed support is still not appearing, stop repeating the same informal conversation forever and use the local education route for the next step.",
            actions: [
              "Use the local escalation or review path in writing.",
            ],
            links: withLinks(
              appendixLinks.educationStart,
              appendixLinks.legalAid,
            ),
          },
        ],
      });
    case "going-back-to-work":
      return buildPlaybook({
        slug,
        title: "Going Back to Work After a Gap",
        description:
          "How to plan a return that is financially and practically sustainable, not just theoretically possible.",
        icon: "💼",
        life_event_tags: ["work", "childcare", "returning to work"],
        steps: [
          {
            title: "Sort childcare before you accept the role if you can",
            description:
              "Childcare, OSHC, and vacation care are often the real make-or-break issue, not the interview itself.",
            actions: [
              "Check the actual care arrangement before you say yes to the job.",
            ],
            links: withLinks(
              { label: "Starting Blocks", url: STARTING_BLOCKS },
              { label: "Services Australia — Child Care Subsidy", url: SERVICES_AUSTRALIA_CHILD_CARE_SUBSIDY },
            ),
          },
          {
            title: "Recheck payments before you start",
            description:
              "Changes to work hours can affect Child Care Subsidy, Parenting Payment, and Family Tax Benefit, so check the real numbers before the first payslip arrives.",
            actions: [
              "Work out the net picture, not just the headline wage.",
            ],
            links: withLinks(
              { label: "Services Australia — Child Care Subsidy", url: SERVICES_AUSTRALIA_CHILD_CARE_SUBSIDY },
              { label: "Services Australia — Parenting Payment", url: SERVICES_AUSTRALIA_PARENTING_PAYMENT },
              { label: "Services Australia — Family Tax Benefit", url: SERVICES_AUSTRALIA_FAMILY_TAX_BENEFIT },
            ),
          },
          {
            title: "Update your CV honestly and calmly",
            description:
              "A caring gap does not need over-explaining. Name it simply, then move quickly to the skills and value you can bring now.",
            actions: [
              "Keep the explanation short and the capability section strong.",
            ],
          },
          {
            title: "Ask how flexibility works in practice",
            description:
              "Start times, finish times, remote work, and school-holiday planning matter more than vague promises about being family friendly.",
            actions: [
              "Ask what flexibility looks like in the real team, not the brochure version.",
            ],
            links: withLinks({ label: "Fair Work — Flexible working arrangements", url: FAIR_WORK_FLEX }),
          },
          {
            title: "Calculate the real cost of returning",
            description:
              "Travel, school holiday care, uniforms, lunches, and payment changes all matter alongside wages.",
            actions: [
              "Do the weekly and school-holiday maths before the start date.",
            ],
          },
          {
            title: "Check the award or agreement before you say yes",
            description:
              "Minimum pay, casual loading, leave, notice, and rostering rules can change whether a job is genuinely workable.",
            actions: [
              "Check the pay and conditions rather than assuming the employer has it right.",
            ],
            links: withLinks({ label: "Fair Work — Pay, awards and entitlements", url: FAIR_WORK_PAY }),
          },
          {
            title: "Build a backup plan",
            description:
              "Sick-child days, school closures, and care breakdowns happen. The backup matters more than the ideal plan.",
            actions: [
              "Name the fallback before you need it.",
            ],
          },
          {
            title: "Let the first version be workable, not perfect",
            description:
              "A safe flexible start is often smarter than chasing the ideal role too fast and burning out by month one.",
            actions: [
              "Optimise for something sustainable enough to keep going.",
            ],
          },
        ],
      });
    case "christmas-one-income":
      return buildPlaybook({
        slug,
        title: "Christmas and School Holidays on One Income",
        description:
          "How to lower the financial pressure without pretending the holidays are easy.",
        icon: "🎄",
        life_event_tags: ["money", "school holidays", "childcare"],
        steps: [
          {
            title: "Put the dates in the calendar early",
            description:
              "School-holiday dates, shutdown periods, and the care gaps hurt less when they are visible sooner.",
            actions: [
              "Map the calendar before December starts making the decisions for you.",
            ],
          },
          {
            title: "Book OSHC or vacation care as early as you can",
            description:
              "Waiting until the last minute is usually more expensive and can leave you with no place left.",
            actions: [
              "Check bookings early if formal care is going to be part of the plan.",
            ],
            links: withLinks({ label: "Starting Blocks", url: STARTING_BLOCKS }),
          },
          {
            title: "Decide the money number before the season decides it for you",
            description:
              "Food, gifts, travel, activities, and care need one budget, not five separate hopes.",
            actions: [
              "Use one realistic number for the whole period.",
            ],
            links: withLinks({ label: "Moneysmart — Budgeting", url: MONEYSMART_BUDGET }),
          },
          {
            title: "Check local free and low-cost options",
            description:
              "Libraries, councils, community centres, and simple routines often do more than expensive plans when children know what to expect.",
            actions: [
              "Choose calm and clear over expensive and frantic.",
            ],
          },
          {
            title: "Avoid high-cost credit for a picture-perfect holiday",
            description:
              "If an essential expense lands, use safer options rather than reaching for payday credit or an expensive short-term fix.",
            actions: [
              "Use safer credit or emergency assistance if you genuinely need help.",
            ],
            links: withLinks(
              { label: "Moneysmart — No Interest Loans", url: MONEYSMART_NILS },
              { label: "National Debt Helpline — Emergency assistance", url: "https://ndh.org.au/debt-solutions/emergency-assistance/" },
            ),
          },
          {
            title: "Tell children the shape of the holiday early",
            description:
              "Certainty is usually kinder than big promises that collapse later under money or time pressure.",
            actions: [
              "Set expectations early and keep them simple.",
            ],
          },
          {
            title: "Spread the load where you can",
            description:
              "Co-parents, grandparents, trusted friends, swap days, and community activities all count as real support.",
            actions: [
              "Ask concretely for help if part of the holiday plan depends on it.",
            ],
          },
          {
            title: "Treat the emotional pressure as real too",
            description:
              "Isolation and pressure spike during holidays. If it is the emotional side that is biting, support is allowed for that too.",
            actions: [
              "Use support earlier instead of waiting for the hardest day.",
            ],
            links: withLinks(
              { label: "Medicare Mental Health", url: MEDICARE_MENTAL_HEALTH },
              { label: "Lifeline", url: LIFELINE },
            ),
          },
        ],
      });
    case "renting-goes-wrong":
      return buildPlaybook({
        slug,
        title: "Your Rights When Renting Goes Wrong",
        description:
          "What to do when the rental is unsafe, unaffordable, hostile, or sliding towards tribunal territory.",
        icon: "🏠",
        life_event_tags: ["housing", "renting", "repairs"],
        steps: [
          {
            title: "Put everything in writing from now on",
            description:
              "Repairs, notices, rent increase objections, hardship requests, and access issues all need dates and evidence once things start going wrong.",
            actions: [
              "Move the dispute into written communication quickly.",
            ],
          },
          {
            title: "Keep one housing file",
            description:
              "Photos, lease, bond details, inspection reports, notices, emails, texts, and receipts should be easy to pull together fast.",
            actions: [
              "Keep all the evidence in one housing folder.",
            ],
          },
          {
            title: "Read the tenancy authority page that applies to you",
            description:
              "Do not rely on UK, US, or generic advice. The right local tenancy authority is the first source when the rental is becoming unsafe or unstable.",
            actions: [
              "Use the rules for your actual state or territory.",
            ],
            links: withLinks(appendixLinks.tenancy),
          },
          {
            title: "Use the urgent-repair process immediately where it applies",
            description:
              "Water, electricity, locks, mould, heating, and major safety issues often have specific urgent-repair pathways.",
            actions: [
              "Use the urgent-repair route instead of waiting for a general reply.",
            ],
            links: withLinks(appendixLinks.tenancy),
          },
          {
            title: "Ask for hardship arrangements early",
            description:
              "Silence usually helps arrears grow faster than your options do, so ask early if rent is becoming impossible.",
            actions: [
              "Raise hardship before the position becomes an eviction problem.",
            ],
            links: withLinks(appendixLinks.tenancy),
          },
          {
            title: "Get advice quickly if a notice arrives",
            description:
              "Whether a notice is valid, how long you have, and what options exist all depend on where you live.",
            actions: [
              "Check the notice against the local rules and get advice fast.",
            ],
            links: withLinks(
              appendixLinks.tenancy,
              appendixLinks.legalAid,
            ),
          },
          {
            title: "Say it out loud if safety or family violence is part of the housing problem",
            description:
              "Some states and territories have specific protections for lease changes and safety, but you usually need to name the safety issue to the right service.",
            actions: [
              "Use family-violence support and the tenancy route in parallel if needed.",
            ],
            links: withLinks(
              { label: "1800RESPECT", url: RESPECT },
              appendixLinks.tenancy,
            ),
          },
          {
            title: "Use the dispute or tribunal pathway with evidence ready",
            description:
              "If you need the tribunal or dispute route, go in with dates, notices, photos, and the correct state or territory process in front of you.",
            actions: [
              "Prepare the file before the hearing, not in the waiting room.",
            ],
            links: withLinks(
              appendixLinks.tenancy,
              appendixLinks.legalAid,
            ),
          },
        ],
      });
    case "benefits-check":
      return buildPlaybook({
        slug,
        title: "Payments Check — Are You Missing Support?",
        description:
          "A calm, repeatable check for the payments and concessions Australian single parents most commonly miss.",
        icon: "💰",
        life_event_tags: ["money", "benefits", "single parent"],
        steps: [
          {
            title: "Start with your current myGov details",
            description:
              "Out-of-date contact, address, rent, or care details cause more delays than people expect.",
            actions: [
              "Make sure your Centrelink and Medicare details match real life now.",
            ],
          },
          {
            title: "Check Parenting Payment and Family Tax Benefit first",
            description:
              "These are often the backbone payments after separation, a drop in work, or a change in care.",
            actions: [
              "Check these first before you assume the system already knows your situation.",
            ],
            links: withLinks(
              { label: "Services Australia — Parenting Payment", url: SERVICES_AUSTRALIA_PARENTING_PAYMENT },
              { label: "Services Australia — Family Tax Benefit", url: SERVICES_AUSTRALIA_FAMILY_TAX_BENEFIT },
            ),
          },
          {
            title: "If you use formal childcare, recheck Child Care Subsidy",
            description:
              "Child Care Subsidy can change when work, income, or care arrangements change, so do not assume last year’s estimate still fits.",
            actions: [
              "Update the estimate when work hours or income shift.",
            ],
            links: withLinks({ label: "Services Australia — Child Care Subsidy", url: SERVICES_AUSTRALIA_CHILD_CARE_SUBSIDY }),
          },
          {
            title: "If you pay rent, recheck Rent Assistance",
            description:
              "Rent changes or address changes can affect the position, and proof of rent may be needed.",
            actions: [
              "Update rent and address details as soon as they change.",
            ],
            links: withLinks({ label: "Services Australia — Rent Assistance", url: SERVICES_AUSTRALIA_RENT_ASSISTANCE }),
          },
          {
            title: "If you are separated, check child support and care percentage",
            description:
              "Care percentages and the child-support setup should reflect what is actually happening, not what used to happen.",
            actions: [
              "Check the recorded care pattern against the real one.",
            ],
            links: withLinks({ label: "Services Australia — Child Support", url: "https://www.servicesaustralia.gov.au/child-support" }),
          },
          {
            title: "If your child has disability or high support needs, check carer supports too",
            description:
              "Do not assume NDIS covers everything. Carer payments and related disability support can matter as much as the school or therapy side.",
            actions: [
              "Check carer supports separately from NDIS.",
            ],
            links: withLinks(
              { label: "Services Australia — Support for carers", url: SERVICES_AUSTRALIA_CARING_SUPPORT },
              { label: "NDIS — Education and school supports", url: NDIS_EDUCATION_SUPPORTS },
            ),
          },
          {
            title: "Check concessions, not just payments",
            description:
              "Health-care cards and local concessions can make a real difference to medicine, transport, utilities, and bills.",
            actions: [
              "Follow the concessions through after the main payment check.",
            ],
            links: withLinks({ label: "Services Australia — Low Income Health Care Card", url: "https://www.servicesaustralia.gov.au/low-income-health-care-card" }),
          },
          {
            title: "Run the check again when something major changes",
            description:
              "New work hours, new rent, separation, a move, childcare changes, diagnosis, or a change in care all justify rerunning the list.",
            actions: [
              "Repeat the check after each big life change instead of only once a year.",
            ],
          },
        ],
      });
    case "co-parenting-hard":
      return buildPlaybook({
        slug,
        title: "Co-Parenting When It Is Hard",
        description:
          "How to keep things child-focused when communication is strained, inconsistent, or hostile.",
        icon: "🤝",
        life_event_tags: ["co-parenting", "separation", "communication"],
        steps: [
          {
            title: "Keep communication brief, factual, and child-focused",
            description:
              "You are not trying to win the relationship argument inside the parenting chat.",
            actions: [
              "Strip messages back to the child, the time, the place, and the next task.",
            ],
          },
          {
            title: "Put the key arrangements in writing",
            description:
              "Days, pickups, holidays, school information, medical details, and who is doing what next are all easier when they are written down.",
            actions: [
              "Write down the arrangement instead of relying on tone and memory.",
            ],
          },
          {
            title: "Keep handovers boring",
            description:
              "Predictable, neutral, and low-drama is the goal. A handover is not the place to revisit the relationship.",
            actions: [
              "Choose the calmest practical handover pattern available.",
            ],
          },
          {
            title: "Do not trade parenting time against money problems",
            description:
              "Child support and parenting arrangements are different systems, and children should not feel the swap happening.",
            actions: [
              "Separate the money conversation from the child-arrangements conversation.",
            ],
            links: withLinks({ label: "Services Australia — Child Support", url: "https://www.servicesaustralia.gov.au/child-support" }),
          },
          {
            title: "Keep records if arrangements repeatedly break down",
            description:
              "Dates, missed time, messages, and practical impact matter more than emotional summaries if you later need help.",
            actions: [
              "Keep the timeline factual and dated.",
            ],
          },
          {
            title: "Use family dispute resolution where it is safe and appropriate",
            description:
              "Many families need a structured third party before anything improves, but safety comes first.",
            actions: [
              "Use a structured route if the pattern is not improving on its own.",
            ],
            links: withLinks({ label: "Family Relationships Online", url: FAMILY_RELATIONSHIPS_ONLINE }),
          },
          {
            title: "Check passport and travel consent early",
            description:
              "Do not leave school tours, overseas trips, or travel-consent questions until the airport week.",
            actions: [
              "Sort consent and paperwork well before travel becomes urgent.",
            ],
            links: withLinks({ label: "Passport Office — Parental consent", url: PASSPORT_CONSENT }),
          },
          {
            title: "If coercion, threats, or fear are part of the picture, stop treating it as ordinary conflict",
            description:
              "When fear or coercion are present, use specialist support and legal help instead of trying to fix it as a communication problem.",
            actions: [
              "Switch from co-parenting tactics to safety support when the pattern changes.",
            ],
            links: withLinks(
              { label: "1800RESPECT", url: RESPECT },
              appendixLinks.legalAid,
            ),
          },
        ],
      });
    case "building-support-network":
      return buildPlaybook({
        slug,
        title: "Building Your Support Network",
        description:
          "When you have moved, separated, burnt out, or realised you cannot carry this alone forever.",
        icon: "💗",
        life_event_tags: ["wellbeing", "connection", "support"],
        steps: [
          {
            title: "Name what is actually missing",
            description:
              "Adult company, after-school backup, practical help, emotional support, and local connection are not all the same gap.",
            actions: [
              "Name the specific kind of support you need first.",
            ],
          },
          {
            title: "Start smaller than your lonely brain tells you to",
            description:
              "One regular, low-pressure thing is usually more useful than five chaotic possibilities.",
            actions: [
              "Pick one repeatable source of connection instead of trying to solve everything at once.",
            ],
          },
          {
            title: "Use the places you already touch",
            description:
              "School gate, childcare, library, sports, community centres, online parent groups, and Kindred all count as real starting points.",
            actions: [
              "Start with the places already in your week.",
            ],
          },
          {
            title: "Ask in concrete terms",
            description:
              "People often respond better to a practical ask than to a general signal that you are struggling.",
            actions: [
              "Try one practical ask rather than a broad apology for needing help.",
            ],
          },
          {
            title: "Reconnect with people you dropped during the hardest stretch",
            description:
              "A simple message often repairs more than you expect once life is a little steadier.",
            actions: [
              "Choose one person and send the message this week.",
            ],
          },
          {
            title: "Treat local services as part of the network too",
            description:
              "A GP, Medicare Mental Health, legal help, financial counselling, and school wellbeing routes all count as support.",
            actions: [
              "Use service support as part of the network instead of saving it for the last crisis.",
            ],
            links: withLinks(
              { label: "Medicare Mental Health", url: MEDICARE_MENTAL_HEALTH },
              appendixLinks.legalAid,
            ),
          },
          {
            title: "Let it take time",
            description:
              "Building a support network from scratch after separation or a move is slow, and that does not mean you are doing it wrong.",
            actions: [
              "Measure progress by consistency, not by instant closeness.",
            ],
          },
          {
            title: "If isolation is turning into hopelessness or crisis, step into support now",
            description:
              "Connection matters, but safety matters first when isolation is sliding into hopelessness, panic, or crisis.",
            actions: [
              "Use support now if the situation is becoming dangerous or unbearable.",
            ],
            links: withLinks(
              { label: "Medicare Mental Health", url: MEDICARE_MENTAL_HEALTH },
              { label: "Lifeline", url: LIFELINE },
            ),
          },
        ],
      });
    default:
      return null;
  }
};
